import {
  Box,
  Button,
  Card,
  Checkbox,
  Field,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Separator,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { FaBangladeshiTakaSign } from "react-icons/fa6";
import { LuClock, LuMapPin } from "react-icons/lu";
import { MdAddCircleOutline, MdDelete } from "react-icons/md";
import { TbCalendarTime } from "react-icons/tb";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import InputField from "../../components/form/InputField";
import { toaster } from "../../components/ui/toaster";
import { useAuth } from "../../contexts/AuthContext";
import {
  useGetEventByIdQuery,
  useGetEventRegistrationQuery,
  useRegisterEventMutation,
  useUpdateRegistrationMutation,
} from "../../features/eventApi";
import { useGetMemberByUserIdQuery } from "../../features/memberApi";
import { calculateAge } from "../../utils/claculateAge";
import { formatTime } from "../../utils/formatTime";
import { defaultEventRegistrationFormValues } from "./defaultEventRegistrationFormValues";
import EventRegistrationPaymentDialog from "./EventRegistrationPaymentDialog";
import {
  EventFormData,
  eventRegistrationSchema,
} from "./eventSchema/eventRegistrationSchema";

const defaultGuest = {
  fullName: "",
  contractNumber: "",
};

export const getFee = (
  eventData: any,
  age?: number,
  relationship?: string,
): number => {
  if (age == null) return 0;

  // Only apply age-based fees for sons and daughters
  const rel = relationship?.toLowerCase();
  if (rel && rel !== "son" && rel !== "daughter") {
    // Other family members (mother, father, spouse, etc.) always pay adult fee
    return eventData?.adult_fee ? Number(eventData.adult_fee) : 0;
  }

  // Age-based fees for sons/daughters ONLY (child under 18, adult 18+)
  if (age < 18) {
    // Child fee - only for son/daughter under 18 years
    return eventData?.child_fee ? Number(eventData.child_fee) : 0;
  } else {
    // Adult fee - for son/daughter 18 years and above
    return eventData?.adult_fee ? Number(eventData.adult_fee) : 0;
  }
};

export const calculateTotalEventFee = (
  eventData: any,
  memberData: any,
  formData: {
    family_members?: string[];
    guests?: any[];
    drivers?: any[];
  },
): number => {
  let totalFee = 0;

  // Member fee
  if (memberData?.id) {
    totalFee += eventData?.member_fee ? Number(eventData.member_fee) : 0;
  }

  // Family members fee
  if (formData.family_members && formData.family_members.length > 0) {
    const selectedFamilyMembers =
      memberData?.family_members?.filter((member: any) =>
        formData.family_members?.includes(member.id),
      ) || [];

    selectedFamilyMembers.forEach((member: any) => {
      const age = calculateAge(member.dateOfBirth);
      totalFee += getFee(eventData, age, member.relationship);
    });
  }

  // Guests fee
  if (formData.guests && formData.guests.length > 0) {
    const guestCount = formData.guests.filter(
      (guest) => guest.fullName && guest.contractNumber,
    ).length;
    totalFee +=
      guestCount * (eventData?.guest_fee ? Number(eventData.guest_fee) : 0);
  }

  // Drivers fee
  if (formData.drivers && formData.drivers.length > 0) {
    const driverCount = formData.drivers.filter(
      (driver) => driver.fullName && driver.contractNumber,
    ).length;
    totalFee +=
      driverCount * (eventData?.driver_fee ? Number(eventData.driver_fee) : 0);
  }

  return totalFee;
};

const EventRegistrationv2 = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get("id");
  const isEditMode = !!registrationId;

  const [open, setOpen] = useState(false);
  const [updateResult, setUpdateResult] = useState<any>(null);
  const formMethods = useForm<EventFormData>({
    resolver: zodResolver(eventRegistrationSchema) as any,
    defaultValues: defaultEventRegistrationFormValues,
  });

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = formMethods;

  // const {
  //   fields: familyFields,
  //   append: appendFamily,
  //   remove: removeFamily,
  // } = useFieldArray({
  //   control,
  //   name: "familyMembers",
  // });

  const {
    fields: guestFields,
    append: appendGuest,
    remove: removeGuest,
  } = useFieldArray({
    control,
    name: "guests",
  });

  const {
    fields: driverFields,
    append: appendDriver,
    remove: removeDriver,
  } = useFieldArray({
    control,
    name: "drivers",
  });

  const [registerEvent, { data, isLoading, isSuccess }] =
    useRegisterEventMutation();

  const [updateRegistration, { isLoading: isUpdating }] =
    useUpdateRegistrationMutation();

  const { userId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminContext = location.pathname.startsWith("/admin");

  const { data: eventData } = useGetEventByIdQuery(eventId || "", {
    skip: !eventId,
  });

  const { data: memberData } = useGetMemberByUserIdQuery(userId || "", {
    skip: !userId || isEditMode,
  });

  const { data: existingRegistration } = useGetEventRegistrationQuery(
    registrationId!,
    { skip: !registrationId },
  );

  const {
    member_fee,
    child_fee,
    adult_fee,
    driver_fee,
    guest_fee,
    event_name,
    event_date,
    event_time,
    event_location,
    last_payment_date,
  } = eventData || {};

  // When in edit mode use the original registrant's member data (for admin editing)
  const effectiveMemberData =
    isEditMode && existingRegistration?.member
      ? existingRegistration.member
      : memberData;

  // Edit is only allowed when payment status is DUE and last payment date hasn't passed
  const editDeadlinePassed = last_payment_date
    ? new Date(last_payment_date + "T00:00:00") <
      new Date(new Date().toDateString())
    : false;
  const isEditPaymentDue =
    !isEditMode ||
    (existingRegistration?.payment?.status === "DUE" && !editDeadlinePassed);

  const driverList =
    effectiveMemberData?.drivers?.map((driver: any) => ({
      name: driver.name,
      id: driver?.id,
      amount: eventData?.driver_fee ? Number(eventData.driver_fee) : 0,
    })) ?? [];

  // clculate height fee of family member fee from family_member_fees

  const family_members =
    effectiveMemberData?.family_members
      ?.filter((i: any) => i.isAlive)
      ?.map((member: any) => {
        return {
          name: member?.name,
          relationship: member?.relationship,
          dateOfBirth: member?.dateOfBirth,
          phone: member?.phone,
          id: member?.id,
          amount: getFee(
            eventData,
            calculateAge(member?.dateOfBirth),
            member?.relationship,
          ),
        };
      }) ?? [];

  // familyMemberList.push(...children);

  const selectedFamilyMembers = watch("family_members") || [];

  const indeterminateFamilyMembers =
    selectedFamilyMembers.length > 0 &&
    selectedFamilyMembers.length < family_members.length;

  // Pre-populate form when in edit mode
  useEffect(() => {
    if (existingRegistration && isEditMode) {
      reset({
        family_members:
          existingRegistration.family_members?.map((m: any) => m.id ?? m) ?? [],
        guests:
          existingRegistration.guests?.map((g: any) => ({
            fullName: g.full_name,
            contractNumber: g.contract_number,
          })) ?? [],
        drivers:
          existingRegistration.drivers?.map((d: any) => ({
            fullName: d.full_name,
            contractNumber: d.contract_number,
          })) ?? [],
      });
    }
  }, [existingRegistration, isEditMode, reset]);

  const onSubmit = async (data: EventFormData) => {
    try {
      if (isEditMode && !isEditPaymentDue) {
        toaster.create({
          title: "Update not allowed",
          description:
            "Registration can only be updated when payment status is DUE.",
          duration: 4000,
          type: "error",
        });
        return;
      }
      if (isEditMode) {
        const result = await updateRegistration({
          registrationId,
          id: registrationId,
          eventId,
          ...data,
          ...(effectiveMemberData?.id && { memberId: effectiveMemberData.id }),
        }).unwrap();

        const totalFee = calculateTotalEventFee(
          eventData,
          effectiveMemberData,
          data,
        );

        if (totalFee > 0 && result?.paymentId) {
          setUpdateResult(result);
          setOpen(true);
          toaster.create({
            title:
              "Registration updated successfully, but payment is still due.",
            duration: 5000,
            type: "success",
          });
        } else {
          toaster.create({
            title: "Registration updated successfully",
            duration: 5000,
            type: "success",
          });
          navigate(
            isAdminContext
              ? `/admin/events/${eventId}/registrations`
              : `/member/events`,
          );
        }
        return;
      }

      // Calculate total fee before submission
      const totalFee = calculateTotalEventFee(eventData, memberData, data);

      const registrationData = {
        eventId: eventId,
        ...data,
        ...(memberData?.id && { memberId: memberData.id }), // Include member ID for backend
      };

      await registerEvent(registrationData).unwrap();
      reset();

      // Only open payment dialog if total fee is greater than 0
      if (totalFee > 0) {
        setOpen(true);
        toaster.create({
          title: "Registration successful, but payment is still due.",
          duration: 5000,
          type: "success",
        });
      } else {
        toaster.create({
          title: "Registration successful",
          duration: 5000,
          type: "success",
        });
        navigate(`/member/events`);
      }

      // Switch to the "My Registrations" tab to show the new registration
      setTimeout(() => {
        const myRegistrationsTab = document.querySelector(
          '[value="my-registrations"]',
        ) as HTMLElement;
        myRegistrationsTab?.click();
      }, 1500);
    } catch (err: any) {
      console.error("Registration error:", err);
      toaster.create({
        title: isEditMode ? "Update failed" : "Registration failed",
        description:
          err?.data?.detail ||
          err?.data?.message ||
          err?.message ||
          "Something went wrong.",
        duration: 4000,
      });
    }
  };

  // Removed useEffect - dialog opening logic now handled in onSubmit based on calculated total fee

  // useEffect(() => {
  //   if (memberData) {
  //     setValue("fullName", memberData.full_name || "");
  //     setValue("phoneNumber", memberData.contact_info?.mobile_tel || "");
  //     setValue("emailAddress", memberData.contact_info?.email || "");
  //     setValue("ibaMembershipNumber", memberData.iba_membership_number || "");
  //   }
  // }, [memberData]);

  const rows = (
    items: {
      id: string;
      relationship?: string;
      name: string;
      dateOfBirth?: string;
      phone?: string;
      amount: number;
    }[],
    name: "family_members",
  ) =>
    items.map((item) => {
      const selectedItems = watch(name) || [];

      return (
        <Table.Row
          key={item.id}
          data-selected={selectedItems.includes(item.id) ? "" : undefined}
        >
          <Table.Cell>
            <Checkbox.Root
              size="sm"
              mt="0.5"
              aria-label="Select row"
              colorPalette={"blue"}
              checked={selectedItems.includes(item.id)}
              onCheckedChange={(changes) => {
                setValue(
                  name,
                  changes.checked
                    ? [...selectedItems, item.id]
                    : selectedItems.filter((id) => id !== item.id),
                );
              }}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
            </Checkbox.Root>
          </Table.Cell>
          <Table.Cell>
            {item.relationship.charAt(0).toUpperCase() +
              item.relationship.slice(1)}
          </Table.Cell>
          <Table.Cell>{item.name}</Table.Cell>
          <Table.Cell>{item.dateOfBirth}</Table.Cell>
          <Table.Cell>{item.phone}</Table.Cell>
          <Table.Cell>
            {getFee(
              eventData,
              calculateAge(item?.dateOfBirth),
              item?.relationship,
            )}
          </Table.Cell>
        </Table.Row>
      );
    });

  return (
    <Box px={{ base: 4, md: 12 }} py={8} bg="white">
      <Flex align="center" justify="space-between" mb={6}>
        <Heading size={"2xl"}>
          {isEditMode ? "Edit Registration" : "Event Registration"}
        </Heading>
      </Flex>

      {isEditMode && !isEditPaymentDue && (
        <Box
          bg="orange.50"
          border="1px solid"
          borderColor="orange.300"
          borderRadius="md"
          p={4}
          mb={4}
        >
          <Text color="orange.700" fontWeight="medium">
            This registration cannot be edited because the payment status is not
            DUE.
          </Text>
        </Box>
      )}

      <Card.Root>
        <Card.Body>
          <Heading size="xl" fontWeight={"bold"} mb={2} color="gray.700">
            {event_name}
          </Heading>
          <HStack gap={6} mb={3} flexWrap="wrap">
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              <Flex align={"center"} gap={1}>
                <Icon as={TbCalendarTime} /> Event Date: {event_date || "N/A"}
              </Flex>
            </Text>
            <Text fontSize="sm" fontWeight="medium" color="red.600">
              <Flex align={"center"} gap={1}>
                <Icon as={TbCalendarTime} /> Registration End:{" "}
                {last_payment_date || "N/A"}
              </Flex>
            </Text>
          </HStack>
          <HStack color="gray.600" fontSize="sm" mb={3}>
            <Flex gap={1} align={"center"}>
              <LuMapPin size={16} />
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                {event_location || "N/A"}
              </Text>
            </Flex>
            {event_time && (
              <Flex align={"center"} gap={1}>
                <LuClock size={16} style={{ marginLeft: "5px" }} />
                <Text fontSize="sm" fontWeight="medium">
                  {formatTime(event_time)}
                </Text>
              </Flex>
            )}
          </HStack>

          <HStack gap={3} mb={4} flexWrap="wrap">
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              <Flex align={"center"} gap={1.2}>
                <Icon as={FaBangladeshiTakaSign} />
                Fees:
              </Flex>
            </Text>
            <HStack gap={3} flexWrap="wrap">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Member: {member_fee || "0"}
              </Text>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Child: {child_fee || "0"}
              </Text>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Adult: {adult_fee || "0"}
              </Text>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Guest: {guest_fee || "0"}
              </Text>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Driver: {driver_fee || "0"}
              </Text>
            </HStack>
          </HStack>
        </Card.Body>
      </Card.Root>

      <Card.Root mt={6}>
        <Card.Body>
          <Heading fontSize="md" color="gray.700">
            Member Information
          </Heading>
          <Flex mt={3} justifyContent={"space-between"} flexWrap="wrap">
            <Text fontSize="sm" color="gray.700">
              Member Name: {memberData?.full_name || "N/A"}{" "}
            </Text>
            <Text fontSize="sm" color="gray.700" ml={6}>
              Phone Number: {memberData?.contact_info?.mobile_tel || "N/A"}
            </Text>
          </Flex>
        </Card.Body>
      </Card.Root>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(1fr)" }}
            gap={3}
            mt={3}
            w={"full"}
          >
            <VStack gap={3} align="stretch" my={3} w={"full"}>
              <Heading size="sm" color="gray.700">
                Family Information
              </Heading>
              <Table.ScrollArea
                borderWidth="1px"
                borderRadius={"md"}
                maxW={{ base: "350px", md: "full" }}
              >
                <Table.Root borderColor="gray.200">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader w="6">
                        <Checkbox.Root
                          size="sm"
                          mt="0.5"
                          aria-label="Select all rows"
                          checked={
                            indeterminateFamilyMembers
                              ? "indeterminate"
                              : selectedFamilyMembers.length > 0
                          }
                          colorPalette={"blue"}
                          onCheckedChange={(changes) => {
                            setValue(
                              "family_members",
                              changes.checked
                                ? family_members.map((item) => item.id)
                                : [],
                            );
                          }}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>Relationships</Table.ColumnHeader>
                      <Table.ColumnHeader>Name</Table.ColumnHeader>
                      <Table.ColumnHeader>Date of Birth</Table.ColumnHeader>
                      <Table.ColumnHeader>Phone Number</Table.ColumnHeader>
                      <Table.ColumnHeader>Fee</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {rows(family_members, "family_members")}
                  </Table.Body>
                </Table.Root>
              </Table.ScrollArea>
            </VStack>
          </Grid>
          <VStack gap={3} align="stretch" my={6}>
            <Flex justify={"space-between"} align="center">
              <Heading size="md" color="gray.700">
                Guests Information
              </Heading>
              {/* <Input
                type="number"
                placeholder="Number of guests"
                w={"25%"}
                onChange={(e) => {
                  const count = e.target.value;
                  const guestsCount = parseInt(count) || 0;
                  const guests = Array.from({ length: guestsCount }, () => ({
                    fullName: "",
                    contractNumber: "",
                  }));
                  setValue("guests", guests);
                }}
              /> */}
              <IconButton
                colorScheme="blue"
                variant="solid"
                size="sm"
                alignSelf="flex-start"
                onClick={() => {
                  appendGuest(defaultGuest);
                  if (Object.keys(errors).length > 0) {
                    trigger("guests");
                  }
                }}
                bg="blue.900"
                color="white"
                _hover={{ bg: "blue.800" }}
              >
                <MdAddCircleOutline />
              </IconButton>
            </Flex>
            <Separator />

            {guestFields.map((field, index) => (
              <Box
                key={field.id}
                p={4}
                bg="gray.50"
                borderRadius="md"
                position="relative"
              >
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={3}
                >
                  <GridItem>
                    <InputField
                      name={`guests.${index}.fullName`}
                      label="Full Name"
                      isRequired
                    />
                  </GridItem>
                  <GridItem>
                    <Field.Root
                      invalid={!!errors.guests?.[index]?.contractNumber}
                    >
                      <Field.Label>
                        Phone Number{" "}
                        <Box as="span" color={"red.600"}>
                          *
                        </Box>
                      </Field.Label>
                      <Flex
                        alignItems={"center"}
                        justifyContent={"center"}
                        w={"full"}
                      >
                        <Input
                          type="text"
                          {...register(`guests.${index}.contractNumber`)}
                          placeholder="Phone Number"
                          bg="white"
                        />

                        <IconButton
                          aria-label="Remove guest"
                          size="sm"
                          colorPalette="red"
                          variant="outline"
                          ml={2}
                          onClick={() => removeGuest(index)}
                        >
                          <MdDelete />
                        </IconButton>
                      </Flex>
                      <Field.ErrorText>
                        {errors.guests?.[index]?.contractNumber?.message}
                      </Field.ErrorText>
                    </Field.Root>
                  </GridItem>
                </Grid>
              </Box>
            ))}
          </VStack>
          <VStack gap={3} align="stretch" my={6}>
            <Flex justify={"space-between"} align="center">
              <Heading size="md" color="gray.700">
                Drivers Information
              </Heading>
              {/* <Input
                type="number"
                placeholder="Number of guests"
                w={"25%"}
                onChange={(e) => {
                  const count = e.target.value;
                  const guestsCount = parseInt(count) || 0;
                  const guests = Array.from({ length: guestsCount }, () => ({
                    fullName: "",
                    contractNumber: "",
                  }));
                  setValue("guests", guests);
                }}
              /> */}
              <IconButton
                colorScheme="blue"
                variant="solid"
                size="sm"
                alignSelf="flex-start"
                onClick={() => {
                  appendDriver(defaultGuest);
                  if (Object.keys(errors).length > 0) {
                    trigger("drivers");
                  }
                }}
                bg="blue.900"
                color="white"
                _hover={{ bg: "blue.800" }}
              >
                <MdAddCircleOutline />
              </IconButton>
            </Flex>
            <Separator />

            {driverFields.map((field, index) => (
              <Box
                key={field.id}
                p={4}
                bg="gray.50"
                borderRadius="md"
                position="relative"
              >
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={3}
                >
                  <GridItem>
                    <InputField
                      name={`drivers.${index}.fullName`}
                      label="Full Name"
                      isRequired
                    />
                  </GridItem>
                  <GridItem>
                    <Field.Root
                      invalid={!!errors.drivers?.[index]?.contractNumber}
                    >
                      <Field.Label>
                        Phone Number{" "}
                        <Box as="span" color={"red.600"}>
                          *
                        </Box>
                      </Field.Label>
                      <Flex
                        alignItems={"center"}
                        justifyContent={"center"}
                        w={"full"}
                      >
                        <Input
                          type="text"
                          {...register(`drivers.${index}.contractNumber`)}
                          placeholder="Phone Number"
                          bg="white"
                        />

                        <IconButton
                          aria-label="Remove guest"
                          size="sm"
                          colorPalette="red"
                          variant="outline"
                          ml={2}
                          onClick={() => removeDriver(index)}
                        >
                          <MdDelete />
                        </IconButton>
                      </Flex>
                      <Field.ErrorText>
                        {errors.guests?.[index]?.contractNumber?.message}
                      </Field.ErrorText>
                    </Field.Root>
                  </GridItem>
                </Grid>
              </Box>
            ))}
          </VStack>
          <Flex justify="flex-end">
            <Button
              type="submit"
              bg="#1A365D"
              color="white"
              size="md"
              px={6}
              loading={isSubmitting || isLoading || isUpdating}
              disabled={isEditMode && !isEditPaymentDue}
            >
              {isEditMode ? "Update Registration" : "Submit Registration"}
            </Button>
          </Flex>
        </form>
      </FormProvider>

      {/* Show Payment Dialog when registration is successful */}
      {/* {showPaymentDialog && data?.paymentId && (
        <DirectPaymentDialog
          paymentId={data.paymentId}
          isOpen={showPaymentDialog}
          onClose={() => setShowPaymentDialog(false)}
        />
      )} */}
      {open && (
        <EventRegistrationPaymentDialog
          open={open}
          setOpen={setOpen}
          paymentId={isEditMode ? updateResult?.paymentId : data?.paymentId}
          eventRegistrationId={isEditMode ? updateResult?.id : data?.id}
          eventData={eventData}
        />
      )}
    </Box>
  );
};

export default EventRegistrationv2;
